import React from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';

import { useAutoFrame } from '@/nova/pages/casino-settings/useAutoFrame';

import type { BrandFlowNode } from '../graph';
import {
  BrandDomainText,
  BrandLogoBox,
  BrandLogoLarge,
  Card,
  HeaderRow,
  PlaceholderText,
  Pill,
} from './nodes.styles';

/** Bare host for display (drop the scheme + a trailing slash), e.g. "https://acme.bet/" -> "acme.bet". */
const displayDomain = (domain: string): string => domain.replace(/^https?:\/\//i, '').replace(/\/+$/, '');

/**
 * The brand identity input card: the logo + site domain that get baked into the ad, shown beneath the
 * key visual and wired into the first beat so the canvas makes the ad's brand inputs explicit.
 */
export const BrandNode = ({ data }: NodeProps<BrandFlowNode>): React.ReactElement => {
  // Pick a light/dark plate by the logo's luminosity so a dark logo doesn't disappear on the dark card
  // (the same treatment every logo preview in the app uses). No plate until there is a logo to judge.
  const frame = useAutoFrame(data.logoUrl ?? '', 'light');

  return (
    <Card $width={220} $status={data.status}>
      <HeaderRow>
        <Pill text="Brand" />
      </HeaderRow>

      <BrandLogoBox $frame={data.logoUrl ? frame : undefined}>
        {data.logoUrl ? <BrandLogoLarge src={data.logoUrl} alt="" /> : <PlaceholderText text="Logo" />}
      </BrandLogoBox>

      {data.domain ? <BrandDomainText text={displayDomain(data.domain)} /> : null}

      <Handle type="source" position={Position.Right} />
    </Card>
  );
};

export default BrandNode;
